import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type {
	ApprovalStepConfig,
	InfoGateStepConfig,
	WorkflowConfig,
	WorkflowStepConfig,
	WorkflowStepType,
} from '@kfolks/types'

/**
 * Workflow-builder shared state provider.
 *
 * Owns the in-progress `WorkflowConfig` (linear step-array contract per
 * ADR-0012) and selection state, exposes mutators consumed by the canvas
 * (task 8.3), the properties panel (8.4), validation badge (8.5–8.6),
 * and the eventual save action (8.9).
 *
 * Mirrors `FormBuilderState`'s shape: page-level context (per design D8 —
 * no global store), simple state slots, no `useQuery` so the provider
 * stays unit-testable without a `QueryClientProvider` ancestor (live
 * validation lives in a separate injector — task 8.5).
 *
 * The shape held here is the **canonical persisted shape** (matches
 * `WorkflowConfig` from `@kfolks/types`), so save → `PUT /requests/types/:id`
 * passes the value through unchanged.
 */
export interface WorkflowBuilderState {
	/** The in-progress workflow. Always a valid `WorkflowConfig` shape. */
	workflowConfig: WorkflowConfig
	/**
	 * Currently-selected step id, or null when no step is selected. Drives
	 * the properties panel's contents. Selection clears on insert / remove
	 * if it was pointing at a removed step (so the properties panel doesn't
	 * dangle).
	 */
	selectedStepId: string | null

	/** Replace the entire config — used on initial load and on Raw JSON edits that round-trip back into Visual. */
	setWorkflowConfig: (next: WorkflowConfig) => void

	/**
	 * Append a new step of the given type at `atIndex` (or at the end when
	 * `atIndex` is `undefined`). Returns the generated step id so callers
	 * can immediately select it. The new step has a unique id of the shape
	 * `<stepType>_<n>` where `n` is the lowest unused suffix in the current
	 * config.
	 */
	insertStep: (stepType: WorkflowStepType, atIndex?: number) => string

	/**
	 * Move the step with id `fromId` to the position currently occupied by
	 * the step with id `toId`. No-op if equal or either id is missing.
	 */
	reorderStep: (fromId: string, toId: string) => void

	/** Remove the step with the given id. Clears selection if it pointed at the removed step. */
	removeStep: (id: string) => void

	/** Select / deselect a step. Pass `null` to clear. */
	selectStep: (id: string | null) => void

	/**
	 * Replace a single step by id (used by the properties panel). The
	 * patch is shallow-merged — the canonical `id` and `type` are preserved
	 * from the original step (changing `type` would invalidate the
	 * discriminated union; renames are a follow-up).
	 */
	updateStep: (id: string, patch: Partial<WorkflowStepConfig>) => void
}

const WorkflowBuilderStateContext = createContext<WorkflowBuilderState | null>(null)

const EMPTY_CONFIG: WorkflowConfig = { version: '1.0', steps: [] }

export function WorkflowBuilderStateProvider({
	initialConfig,
	children,
}: {
	initialConfig?: WorkflowConfig
	children: ReactNode
}) {
	const [workflowConfig, setWorkflowConfig] = useState<WorkflowConfig>(initialConfig ?? EMPTY_CONFIG)
	const [selectedStepId, setSelectedStepId] = useState<string | null>(null)

	const insertStep = useCallback((stepType: WorkflowStepType, atIndex?: number): string => {
		let inserted = ''
		setWorkflowConfig((prev) => {
			const id = generateUniqueStepId(stepType, prev.steps)
			const newStep = stubStepFor(stepType, id)
			const idx = atIndex === undefined || atIndex < 0 || atIndex > prev.steps.length ? prev.steps.length : atIndex
			const steps = [...prev.steps.slice(0, idx), newStep, ...prev.steps.slice(idx)]
			inserted = id
			return { ...prev, steps }
		})
		return inserted
	}, [])

	const reorderStep = useCallback((fromId: string, toId: string) => {
		if (fromId === toId) return
		setWorkflowConfig((prev) => {
			const fromIndex = prev.steps.findIndex((s) => s.id === fromId)
			const toIndex = prev.steps.findIndex((s) => s.id === toId)
			if (fromIndex === -1 || toIndex === -1) return prev
			const next = [...prev.steps]
			const [moved] = next.splice(fromIndex, 1)
			if (!moved) return prev
			next.splice(toIndex, 0, moved)
			return { ...prev, steps: next }
		})
	}, [])

	const removeStep = useCallback((id: string) => {
		setWorkflowConfig((prev) => ({ ...prev, steps: prev.steps.filter((s) => s.id !== id) }))
		setSelectedStepId((prev) => (prev === id ? null : prev))
	}, [])

	const selectStep = useCallback((id: string | null) => {
		setSelectedStepId(id)
	}, [])

	const updateStep = useCallback((id: string, patch: Partial<WorkflowStepConfig>) => {
		setWorkflowConfig((prev) => ({
			...prev,
			steps: prev.steps.map((s) =>
				s.id === id
					? // Preserve the canonical `id` and `type` — changing `type` would
						// break the discriminated union; any rename is a follow-up that
						// also has to update every `after.next` reference.
						({ ...s, ...patch, id: s.id, type: s.type } as WorkflowStepConfig)
					: s,
			),
		}))
	}, [])

	const value = useMemo<WorkflowBuilderState>(
		() => ({
			workflowConfig,
			selectedStepId,
			setWorkflowConfig,
			insertStep,
			reorderStep,
			removeStep,
			selectStep,
			updateStep,
		}),
		[workflowConfig, selectedStepId, insertStep, reorderStep, removeStep, selectStep, updateStep],
	)

	return <WorkflowBuilderStateContext.Provider value={value}>{children}</WorkflowBuilderStateContext.Provider>
}

export function useWorkflowBuilderState(): WorkflowBuilderState {
	const ctx = useContext(WorkflowBuilderStateContext)
	if (!ctx) throw new Error('useWorkflowBuilderState must be used within a WorkflowBuilderStateProvider')
	return ctx
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Generate a unique id of the shape `<stepType>_<n>` where `n` is the
 * lowest unused integer (starting at 1) that doesn't collide with any
 * existing step id. Predictable + readable so admins recognise their
 * own steps in `after.next` references and timeline views.
 */
function generateUniqueStepId(stepType: WorkflowStepType, existing: ReadonlyArray<WorkflowStepConfig>): string {
	const taken = new Set(existing.map((s) => s.id))
	let n = 1
	while (taken.has(`${stepType}_${n}`)) n++
	return `${stepType}_${n}`
}

/**
 * Build a fresh, minimally-valid step stub for the given type. The stub
 * deliberately omits required-by-the-engine fields (the four approver
 * dimensions for `approval`; `fields[]` for `info_gate`) so the live
 * validator immediately surfaces "configure this step" errors — better
 * than silently producing a step that says everything's fine but won't
 * run.
 */
function stubStepFor(stepType: WorkflowStepType, id: string): WorkflowStepConfig {
	switch (stepType) {
		case 'approval': {
			const stub: ApprovalStepConfig = {
				id,
				type: 'approval',
				label: humanLabelFor(stepType),
			}
			return stub
		}
		case 'info_gate': {
			const stub: InfoGateStepConfig = {
				id,
				type: 'info_gate',
				label: humanLabelFor(stepType),
				mode: 'manual',
				fields: [],
			}
			return stub
		}
		default: {
			const _exhaustive: never = stepType
			void _exhaustive
			throw new Error(`Unknown workflow step type: ${String(stepType)}`)
		}
	}
}

function humanLabelFor(stepType: WorkflowStepType): string {
	switch (stepType) {
		case 'approval':
			return 'Approval'
		case 'info_gate':
			return 'Info gate'
		default: {
			const _exhaustive: never = stepType
			void _exhaustive
			return 'Step'
		}
	}
}
